// SkillExecutionDetailModal — modal de detalhe de UMA execução de skill.
//
// SKL-B (admin 2026-06-12): redesign completo na linguagem visual do wireframe
// (mesmo padrão da modal de Atividade — era "bordas sobre bordas", caixas
// coloridas e JSON cru). Anatomia:
//   header wf-modal-header — ícone + skill vX + badge de status + ✕
//   métricas wf-metricgrid — Duração · Tokens · Custo · Steps
//   tabs wf-tabs           — Linha do tempo · Input · Output
//   Linha do tempo         — wf-tl (dot por status) + acordeão por step com
//                            input/output em DataBlock (árvore por nível)
//   Input/Output/Erro      — Field + DataBlock/SkillOutputRenderer (markdown,
//                            imagens e artefatos renderizados de verdade)
//
// Dados: api.getSkillExecutionDetail(id) → { execution, steps[], skill }.

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { Activity, X } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '@/lib/utils';
import { fmtDateTime, formatCostStrict, formatTokens } from '../lib/format';
import { formatDurationMs } from '@/components/skills/skill-constants';
import { WfMetric } from '@/components/wf';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DataBlock, Field, JsonNode, PreBlock } from '@/components/activity/json-view';
import { extractEmbeddedJson, looksLikeMd, rawText, tryParseJson } from '@/components/activity/json-view-utils';
import { SkillOutputRenderer } from './SkillOutputRenderer';
import '@/styles/wf-activity.css';
import '@/styles/wf-demands.css';

type Tab = 'timeline' | 'input' | 'output';

const TABS: Array<{ value: Tab; label: string }> = [
  { value: 'timeline', label: 'Linha do tempo' },
  { value: 'input', label: 'Input' },
  { value: 'output', label: 'Output' },
];

/** Status da execução/step → vocabulário curto + tom do wf-dot. */
const STATUS_PT: Record<string, string> = {
  running: 'em execução',
  ok: 'ok',
  failed: 'falha',
  partial: 'parcial',
  canceled: 'cancelado',
  cancelled: 'cancelado',
};

function statusDot(status: string | null | undefined): string | null {
  if (status === 'ok') return 'wf-dot--ok';
  if (status === 'running') return 'wf-dot--run';
  if (status === 'failed') return 'wf-dot--bad';
  if (status === 'partial') return 'wf-dot--wait';
  return null;
}

export interface SkillExecutionDetailModalProps {
  executionId: string | null;
  open: boolean;
  onClose: () => void;
}

type DetailData = Awaited<ReturnType<typeof api.getSkillExecutionDetail>>;
type StepRow = DetailData['steps'][number];

export function SkillExecutionDetailModal({ executionId, open, onClose }: SkillExecutionDetailModalProps) {
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('output');

  useEffect(() => {
    if (!open || !executionId) return;
    setLoading(true);
    setError(null);
    setData(null);
    void (async () => {
      try {
        const result = await api.getSkillExecutionDetail(executionId);
        setData(result);
        // Default: Linha do tempo quando há steps (progresso é o que importa);
        // senão Output; Input como último recurso.
        if (result.steps.length > 0) setActiveTab('timeline');
        else if (result.execution.output) setActiveTab('output');
        else setActiveTab('input');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, executionId]);

  const exec = data?.execution;
  const tokens = exec ? (exec.tokens_input ?? 0) + (exec.tokens_output ?? 0) : 0;
  const cost = (exec as unknown as { cost_usd?: number | null } | undefined)?.cost_usd;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        size="xl"
        hideClose
        className="max-h-[90vh] w-[92vw] max-w-[860px] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <DialogTitle className="sr-only">Detalhes da execução</DialogTitle>
        <DialogDescription className="sr-only">
          Execução da skill {data?.skill?.name ?? ''}
        </DialogDescription>

        {/* Header FIXO (wireframe .dl-h) */}
        <div className="wf-modal-header shrink-0">
          <Activity className="ic" style={{ color: 'var(--wf-accent-text)' }} aria-hidden="true" />
          <span className="wf-row-code">
            {data?.skill ? `${data.skill.name} · v${data.skill.version}` : 'Execução'}
          </span>
          {exec?.status && (
            <span className="text-xs text-raios-text-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span className={cn('wf-dot', statusDot(exec.status))} aria-hidden="true" />
              {STATUS_PT[exec.status] ?? exec.status}
            </span>
          )}
          <button type="button" className="wf-modal-close" onClick={onClose} aria-label="Fechar">
            <X className="ic" aria-hidden="true" />
          </button>
        </div>

        {/* Corpo rolável */}
        <div className="wf-dm-panel" style={{ maxHeight: 'none', flex: 1 }}>
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full rounded-lg" />
              <Skeleton className="h-40 w-full rounded-lg" />
            </div>
          )}

          {error && (
            <Field label="Erro ao carregar" copy={error}>
              <DataBlock value={error} variant="danger" />
            </Field>
          )}

          {exec && !loading && (
            <>
              {/* Métricas (wireframe .mxgrid) */}
              {/* MTR-03: WfMetric — valor encolhe pra caber na caixinha. */}
              <div className="wf-metricgrid" style={{ marginBottom: 14 }}>
                <WfMetric value={formatDurationMs(exec.duration_ms)} label="Duração" />
                <WfMetric value={tokens > 0 ? (formatTokens(tokens) ?? '0') : '—'} label="Tokens" />
                <WfMetric
                  value={typeof cost === 'number' ? formatCostStrict(cost) : '—'}
                  label="Custo"
                />
                <WfMetric value={data!.steps.length ? String(data!.steps.length) : '—'} label="Steps" />
              </div>

              {/* Quando + trigger (uma linha discreta, sem caixa) */}
              <p className="text-[11px] text-raios-muted mb-3" style={{ fontFamily: 'var(--wf-mono)' }}>
                {fmtDateTime(exec.started_at)}
                {exec.invoker_type ? ` · disparada via ${exec.invoker_type}` : ''}
                {exec.caller_channel ? ` (${exec.caller_channel})` : ''}
                {(exec as unknown as { trigger_summary?: string | null }).trigger_summary
                  ? ` · ${(exec as unknown as { trigger_summary?: string | null }).trigger_summary}`
                  : ''}
              </p>

              {/* Tabs (wireframe .wf-tabs) */}
              <div className="wf-tabs" style={{ marginBottom: 14 }}>
                {TABS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className={cn('wf-tab', activeTab === t.value && 'active')}
                    onClick={() => setActiveTab(t.value)}
                  >
                    {t.label}
                    {t.value === 'timeline' && data!.steps.length > 0 ? ` (${data!.steps.length})` : ''}
                  </button>
                ))}
              </div>

              {activeTab === 'timeline' && (
                <TimelineTab steps={data!.steps} executionStartedAt={exec.started_at} />
              )}
              {activeTab === 'input' && (
                <Field label="Input" copy={rawText(exec.input)}>
                  <DataBlock value={exec.input ?? '— sem input registrado'} />
                </Field>
              )}
              {activeTab === 'output' && (
                <OutputTab
                  output={exec.output}
                  error={exec.error}
                  status={exec.status}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Conteúdo rico (SKL-D) ──────────────────────────────────────────────────── */

/** URL "pura" (uma linha só) → renderer detecta imagem/arquivo. */
function isBareUrl(s: string): boolean {
  return /^https?:\/\/\S+$/i.test(s.trim());
}

function MdView({ text }: { text: string }) {
  return (
    <div className="wf-mdview prose prose-sm dark:prose-invert max-w-none break-words">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

/**
 * Conteúdo string de input/output: decide a MELHOR visualização — JSON puro →
 * árvore; texto com JSON embutido → md/texto ao redor + árvore; markdown →
 * prose legível; URL → renderer (imagem/arquivo); texto → pre. UMA borda só
 * (admin: "bordas duplas, horrível"; "MD tá péssimo de ler, tudo colado").
 */
function StringContent({ text }: { text: string }) {
  const parsed = tryParseJson(text);
  if (parsed !== null && typeof parsed === 'object') {
    return <DataBlock value={parsed} />;
  }
  const embedded = extractEmbeddedJson(text);
  if (embedded) {
    return (
      <div className="space-y-2">
        {embedded.before &&
          (looksLikeMd(embedded.before) ? (
            <MdView text={embedded.before} />
          ) : (
            <PreBlock value={embedded.before} />
          ))}
        <div className="wf-acpre wf-jt">
          <JsonNode name={null} value={embedded.obj} depth={0} />
        </div>
        {embedded.after &&
          (looksLikeMd(embedded.after) ? (
            <MdView text={embedded.after} />
          ) : (
            <PreBlock value={embedded.after} />
          ))}
      </div>
    );
  }
  if (isBareUrl(text)) {
    return <SkillOutputRenderer output={text} contained={false} />;
  }
  if (looksLikeMd(text)) {
    return <MdView text={text} />;
  }
  return <PreBlock value={text} />;
}

/* ── Linha do tempo ─────────────────────────────────────────────────────────── */

function TimelineTab({
  steps,
  executionStartedAt,
}: {
  steps: StepRow[];
  executionStartedAt: string;
}) {
  if (steps.length === 0) {
    return (
      <p className="text-sm text-raios-text-secondary">
        Nenhum step registrado — execução em modo legado (sem persistência por step).
      </p>
    );
  }
  const startMs = new Date(executionStartedAt).getTime();

  return (
    <div className="wf-tl">
      {steps.map((step) => {
        const stepStart = new Date(step.started_at).getTime();
        const stepEnd = step.ended_at ? new Date(step.ended_at).getTime() : null;
        const offsetMs = stepStart - startMs;
        const durationMs = stepEnd ? stepEnd - stepStart : null;
        // Output persistido como { text, model } pelo skill-runner — extrai pra
        // renderizar limpo; outro shape → DataBlock genérico.
        const stepOut = step.output as Record<string, unknown> | string | null;
        const isWrapped =
          typeof stepOut === 'object' && stepOut !== null &&
          'text' in stepOut && typeof (stepOut as { text: unknown }).text === 'string';
        const outValue = isWrapped ? (stepOut as { text: string }).text : stepOut;
        const stepModel = isWrapped ? (stepOut as { model?: string }).model : undefined;
        const hasDetails =
          (step.input && Object.keys(step.input).length > 0) || outValue != null || step.error != null;

        return (
          <div key={step.id} className="wf-tlr" style={{ alignItems: 'flex-start' }}>
            <span
              className={cn('wf-tldot', statusDot(step.status) ?? undefined)}
              aria-hidden="true"
              style={{ marginTop: 5 }}
            />
            <div className="wf-tlmsg" style={{ flex: 1, minWidth: 0 }}>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-semibold text-raios-text">
                  {step.step_number}. {step.step_name}
                </span>
                <span className="text-[11px] text-raios-muted tabular-nums">
                  {STATUS_PT[step.status] ?? step.status}
                  {offsetMs >= 0 && ` · +${formatDurationMs(offsetMs)}`}
                  {durationMs != null && ` · ${formatDurationMs(durationMs)}`}
                  {step.tokens_used != null && step.tokens_used > 0 &&
                    ` · ${formatTokens(step.tokens_used) ?? step.tokens_used} tokens`}
                  {stepModel && ` · ${stepModel}`}
                </span>
              </div>

              {step.error != null && (
                <div className="mt-2">
                  <Field label="Erro do step" copy={rawText(step.error)}>
                    <DataBlock value={step.error} variant="danger" />
                  </Field>
                </div>
              )}

              {hasDetails && step.error == null && (
                <details className="mt-1.5">
                  <summary className="text-[11px] text-raios-muted cursor-pointer hover:text-raios-text select-none">
                    ver input/output do step
                  </summary>
                  <div className="mt-2 space-y-2">
                    {step.input && Object.keys(step.input).length > 0 && (
                      <Field label="Input do step" copy={rawText(step.input)}>
                        <DataBlock value={step.input} />
                      </Field>
                    )}
                    {outValue != null && (
                      <Field label="Output do step" copy={rawText(outValue)}>
                        {typeof outValue === 'string' ? (
                          <StringContent text={outValue} />
                        ) : (
                          <DataBlock value={outValue} />
                        )}
                      </Field>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Output ─────────────────────────────────────────────────────────────────── */

function OutputTab({
  output,
  error,
  status,
}: {
  output: Record<string, unknown> | null | undefined;
  error: Record<string, unknown> | null | undefined;
  status: string;
}) {
  const finalOutput = (output as { final_output?: unknown } | null | undefined)?.final_output;
  const artifacts = (output as { artifacts?: unknown[] } | null | undefined)?.artifacts;
  const hasArtifacts = Array.isArray(artifacts) && artifacts.length > 0;
  const mainOutput = finalOutput !== undefined ? finalOutput : output;

  return (
    <div className="space-y-3">
      {status === 'failed' && error != null && (
        <Field label="Erro" copy={rawText(error)}>
          <DataBlock value={error} variant="danger" />
        </Field>
      )}

      {hasArtifacts && <SkillOutputRenderer output={{ artifacts }} contained={false} />}

      {mainOutput != null ? (
        <Field
          label={finalOutput !== undefined ? 'Output final' : 'Output'}
          copy={rawText(mainOutput)}
        >
          {typeof mainOutput === 'string' ? (
            <StringContent text={mainOutput} />
          ) : (
            <DataBlock value={mainOutput} />
          )}
        </Field>
      ) : (
        status !== 'failed' && (
          <p className="text-sm text-raios-text-secondary">Nenhum output ainda.</p>
        )
      )}

      {/* Payload bruto completo — debug, colapsado */}
      {finalOutput !== undefined && output != null && (
        <details>
          <summary className="text-[11px] text-raios-muted cursor-pointer hover:text-raios-text select-none">
            ver payload bruto completo (debug)
          </summary>
          <div className="mt-2">
            <Field label="Payload bruto" copy={rawText(output)}>
              <DataBlock value={output} />
            </Field>
          </div>
        </details>
      )}
    </div>
  );
}
